import { ClientTagBadges } from "../../ClientTags/ClientTags";
import { canManageClientTags } from "intake_shared/src/client_tags";
import {
    ClientStatus,
    DateFormats,
    IArchivedClient,
    IClient,
} from "intake_shared/src/types";
import * as React from "react";
import { TableHeader } from "../../../../../types";
import moment from "../../../../../utils/app_moment";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { Badge } from "../../../bits/Badge/Badge";
import { displayPhoneNumber, toTitleCase } from "../../../../../utils/utils";
import {
    getClientRowStylesForInquiryMethod,
    getColorStylesForClientStatus,
    getColorStylesForInquiryMethod,
} from "../../../../../constants";
import { AclContext } from "../../../../../contexts/AclContext";
import Table from "../../../bits/Table/Table";
import styles from "./ClientMasterList.module.scss";
import ShortenedText from "../../../bits/ShortenedText/ShortenedText";
import CompletionReasonEditor from "../../CompletionReasonEditor/CompletionReasonEditor";

interface ClientMasterListProps {
    clients: IClient[];
    allowSelect: boolean;
    pageNumber?: number;
    onSelectActions?: (rows: IClient[]) => JSX.Element[];
    onCompletionReasonSaved?: (clientId: string, reason: string) => void;
}

export default function ClientMasterList({
    clients,
    allowSelect,
    pageNumber = 0,
    onSelectActions,
    onCompletionReasonSaved,
}: ClientMasterListProps) {
    const browserLocation = useLocation();
    const aclContext = React.useContext(AclContext);

    const columns = React.useMemo(() => {
        let headers: TableHeader[] = [];
        if (
            clients.some(
                (client) =>
                    "archived_on" in client &&
                    client.status === ClientStatus.COMPLETED
            )
        ) {
            headers.push({
                Header: "Closed on",
                id: "archived_on",
                accessor: (row: IArchivedClient) => (
                    <span>
                        {row.archived_on && row.status === ClientStatus.COMPLETED
                            ? moment(row.archived_on).format(
                                  DateFormats.DAY_MONTH_YEAR_FORMAT
                              )
                            : "Not Closed"}
                    </span>
                ),
            });
            headers.push({
                Header: "Status",
                id: "status",
                accessor: (row: IClient) => (
                    <Badge
                        content={row.status}
                        textColor="#ffffff"
                        bgColor={getColorStylesForClientStatus(row.status).bgColor}
                    />
                ),
            });
        }
        headers.push(
            ...[
                {
                    Header: "Unique ID",
                    id: "unique_client_id",
                    accessor: (row: IClient) => {
                        const clientId = row.id || "";
                        const shortId = clientId ? clientId.slice(0, 6) : "--";
                        return (
                            <span>
                                <strong>{shortId}</strong>
                            </span>
                        );
                    },
                },
                {
                    Header: "Inquiry method",
                    id: "inquiry_method",
                    accessor: (row: IClient) => (
                        <span>
                            {row.inquiry_method ? (
                                <Badge
                                    content={toTitleCase(row.inquiry_method)}
                                    textColor="#ffffff"
                                    bgColor={
                                        getColorStylesForInquiryMethod(
                                            row.inquiry_method
                                        ).bgColor
                                    }
                                />
                            ) : (
                                "--"
                            )}
                        </span>
                    ),
                },
                {
                    Header: "Email",
                    accessor: "email",
                },
                {
                    Header: "Phone no",
                    id: "phone_no",
                    accessor: (row: IClient) => (
                        <span className={styles.phoneNo}>
                            {row.phone_no ? displayPhoneNumber(row.phone_no) : "--"}
                        </span>
                    ),
                },
                {
                    Header: "First name",
                    accessor: "first_name",
                },
                {
                    Header: "Last name",
                    accessor: "last_name",
                },
                {
                    Header: "Pronoun",
                    id: "pronouns",
                    accessor: (row: IClient) => <span>{row.pronouns || "--"}</span>,
                },
            ]
        );
        if (
            clients.some(
                (client) => client.status === ClientStatus.INCOMPLETE_INQUIRY
            )
        ) {
            headers.push({
                Header: "Follow-ups",
                id: "ins_follow_ups",
                accessor: (row: IClient) => (
                    <>
                        {" "}
                        {row[ClientStatus.INCOMPLETE_INQUIRY]
                            ?.notes_for_incomplete_inquiry ? (
                            <ShortenedText
                                notes={
                                    row[ClientStatus.INCOMPLETE_INQUIRY]
                                        ?.notes_for_incomplete_inquiry
                                }
                                identifyingKey={`followups-${row.id || row.email}`}
                            />
                        ) : (
                            "--"
                        )}{" "}
                    </>
                ),
            });
        }
        if (clients.some((client) => client.status === ClientStatus.FOLLOWUP)) {
            headers.push(
                {
                    Header: "Followup Start",
                    id: "follow-start-followup",
                    accessor: (row: IClient) => (
                        <p>
                            {moment(row[ClientStatus.FOLLOWUP]?.start_date).format(
                                DateFormats.DAY_MONTH_YEAR_FORMAT
                            )}
                        </p>
                    ),
                },
                {
                    Header: "Followup End",
                    id: "follow-end-followup",
                    accessor: (row: IClient) => (
                        <p>
                            {row[ClientStatus.FOLLOWUP]?.ending_date
                                ? moment(
                                      row[ClientStatus.FOLLOWUP]?.ending_date
                                  ).format(DateFormats.DAY_MONTH_YEAR_FORMAT)
                                : "--"}
                        </p>
                    ),
                },
                {
                    Header: "Follow-ups",
                    id: "follow-ups-followup",
                    accessor: (row: IClient) => (
                        <>
                            {row[ClientStatus.FOLLOWUP]?.followup_notes ? (
                                <ShortenedText
                                    notes={
                                        row[ClientStatus.FOLLOWUP]?.followup_notes
                                    }
                                    identifyingKey={`followups-${
                                        row.id || row.email
                                    }`}
                                />
                            ) : (
                                "--"
                            )}
                        </>
                    ),
                }
            );
        }
        if (clients.some((client) => client.status === ClientStatus.APPT_SETTING)) {
            headers.push(
                {
                    Header: "Followup Start",
                    id: "follow-start-appt-setting",
                    accessor: (row: IClient) => (
                        <p>
                            {row[ClientStatus.FOLLOWUP]?.start_date
                                ? moment(
                                      row[ClientStatus.FOLLOWUP]?.start_date
                                  ).format(DateFormats.DAY_MONTH_YEAR_FORMAT)
                                : "--"}
                        </p>
                    ),
                },
                {
                    Header: "Followup End",
                    id: "follow-end-appt-setting",
                    accessor: (row: IClient) => (
                        <p>
                            {row[ClientStatus.FOLLOWUP]?.ending_date
                                ? moment(
                                      row[ClientStatus.FOLLOWUP]?.ending_date
                                  ).format(DateFormats.DAY_MONTH_YEAR_FORMAT)
                                : "--"}
                        </p>
                    ),
                },
                {
                    Header: "Follow-up Notes",
                    id: "follow-up-notes-appt-setting",
                    accessor: (row: IClient) => (
                        <>
                            {row[ClientStatus.FOLLOWUP]?.followup_notes ? (
                                <ShortenedText
                                    notes={
                                        row[ClientStatus.FOLLOWUP]?.followup_notes
                                    }
                                    identifyingKey={`followup-${
                                        row.id || row.email
                                    }`}
                                />
                            ) : (
                                "--"
                            )}
                        </>
                    ),
                },
                {
                    Header: "Other Follow-up Notes",
                    id: "other-follow-up-notes-appt-setting",
                    accessor: (row: IClient) => (
                        <>
                            {row[ClientStatus.APPT_SETTING]?.follow_ups ? (
                                <ShortenedText
                                    notes={
                                        row[ClientStatus.APPT_SETTING]?.follow_ups
                                    }
                                    identifyingKey={`followups-${
                                        row.id || row.email
                                    }`}
                                />
                            ) : (
                                "--"
                            )}
                        </>
                    ),
                }
            );
        }
        if (clients.some((client) => client.status === ClientStatus.FEE_SETTING)) {
            headers.push({
                Header: "Follow-ups",
                id: "fee_follow_ups",
                accessor: (row: IClient) => (
                    <>
                        {row[ClientStatus.APPT_SETTING]?.follow_ups ? (
                            <ShortenedText
                                notes={row[ClientStatus.APPT_SETTING]?.follow_ups}
                                identifyingKey={`followups-${row.id || row.email}`}
                            />
                        ) : (
                            "--"
                        )}
                    </>
                ),
            });
        }
        if (clients.some((client) => client.status !== ClientStatus.COMPLETED)) {
            headers.push(
                ...[
                    {
                        Header: "Insurance",
                        accessor: "insurance_provider",
                        canSort: true,
                    },
                    {
                        Header: "Status",
                        id: "status_",
                        accessor: (row: IClient) => (
                            <Badge
                                content={row.status}
                                textColor="#ffffff"
                                bgColor={
                                    getColorStylesForClientStatus(row.status).bgColor
                                }
                            />
                        ),
                    },
                    {
                        Header: "Assignees",
                        id: "assignees",
                        accessor: (row: IClient) => {
                            const primary = row.assigned_to;
                            const secondary = row.secondary_assignee;

                            if (!primary && !secondary) {
                                return <span>--</span>;
                            }

                            return (
                                <div className={styles.assignees}>
                                    <span className={styles.srOnly}>
                                        {primary && `Primary: ${primary}`}
                                        {primary && secondary && ", "}
                                        {secondary && `Secondary: ${secondary}`}
                                        {!primary && !secondary && "No assignees"}
                                    </span>

                                    {primary && (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip id={`primary-${row.id}`}>
                                                    Primary: {primary}
                                                </Tooltip>
                                            }
                                        >
                                            <div
                                                className={styles.assigneeAvatar}
                                                style={{
                                                    borderColor:
                                                        aclContext?.intakeConfig
                                                            ?.intakeOfficerColors?.[
                                                            primary
                                                        ] ||
                                                        "var(--color-primary-v2)",
                                                    color:
                                                        aclContext?.intakeConfig
                                                            ?.intakeOfficerColors?.[
                                                            primary
                                                        ] ||
                                                        "var(--color-primary-v2)",
                                                }}
                                            >
                                                {primary.charAt(0).toUpperCase()}
                                            </div>
                                        </OverlayTrigger>
                                    )}
                                    {secondary && (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip id={`secondary-${row.id}`}>
                                                    Secondary: {secondary}
                                                </Tooltip>
                                            }
                                        >
                                            <div
                                                className={styles.assigneeAvatar}
                                                style={{
                                                    borderColor:
                                                        aclContext?.intakeConfig
                                                            ?.intakeOfficerColors?.[
                                                            secondary
                                                        ] ||
                                                        "var(--color-primary-v2)",
                                                    color:
                                                        aclContext?.intakeConfig
                                                            ?.intakeOfficerColors?.[
                                                            secondary
                                                        ] ||
                                                        "var(--color-primary-v2)",
                                                }}
                                            >
                                                {secondary.charAt(0).toUpperCase()}
                                            </div>
                                        </OverlayTrigger>
                                    )}
                                </div>
                            );
                        },
                    },
                ]
            );
        }
        if (clients.some((client) => client.status === ClientStatus.COMPLETED)) {
            headers.push(
                ...[
                    {
                        Header: "Completion Reason",
                        accessor: (row: IClient) => (
                            <span className={styles.reasonCell}>
                                {row.status === ClientStatus.COMPLETED ? (
                                    <>
                                        <ShortenedText
                                            notes={row.complete_notes.reason}
                                            identifyingKey={`completion-reason-${row.id}`}
                                        />
                                        {onCompletionReasonSaved && (
                                            <CompletionReasonEditor
                                                client={row as IArchivedClient}
                                                onSaved={(reason) =>
                                                    onCompletionReasonSaved(
                                                        row.id,
                                                        reason
                                                    )
                                                }
                                            />
                                        )}
                                    </>
                                ) : (
                                    "--"
                                )}
                            </span>
                        ),
                    },
                    {
                        Header: "Completion Notes",
                        accessor: (row: IClient) => (
                            <span>
                                {row.status === ClientStatus.COMPLETED &&
                                row.complete_notes.notes ? (
                                    <ShortenedText
                                        notes={row.complete_notes.notes}
                                        identifyingKey={`completion-notes-${row.id}`}
                                    />
                                ) : (
                                    "--"
                                )}
                            </span>
                        ),
                    },
                ]
            );
        }
        headers.push({
            Header: "",
            id: "view-client-from-list",
            accessor: (row: IClient) => (
                <>
                    {row.status === ClientStatus.COMPLETED ? (
                        <Link
                            to={{
                                pathname: "/v2/view-inactive-client",
                                search: `?clientId=${row.id}`,
                                state: {
                                    client: row,
                                    returnPathname: browserLocation.pathname,
                                    returnSearch: browserLocation.search,
                                    returnState: browserLocation.state,
                                },
                            }}
                        >
                            View
                        </Link>
                    ) : (
                        <Link
                            to={{
                                pathname: `/v2/client-full-view`,
                                search: `?clientId=${row.id}&activeTab=${
                                    row.status
                                }&beforeId=${clients[
                                    clients.length - 1
                                ]?.date_updated?.toISOString()}&page=${pageNumber}`,
                                state: {
                                    client: row,
                                    returnPathname: browserLocation.pathname,
                                    returnSearch: browserLocation.search,
                                    returnState: browserLocation.state,
                                },
                            }}
                        >
                            View
                        </Link>
                    )}
                </>
            ),
        });
        if (canManageClientTags(aclContext?.authUser?.acl)) {
            headers.splice(headers.length - 1, 0, {
                Header: "Tags",
                id: "client_tags",
                accessor: (row: IClient) => (
                    <ClientTagBadges tags={row.tags} compact />
                ),
            });
        }
        return headers;
    }, [
        aclContext?.intakeConfig?.intakeOfficerColors,
        aclContext?.authUser?.acl,
        browserLocation.pathname,
        browserLocation.search,
        browserLocation.state,
        clients,
        onCompletionReasonSaved,
        pageNumber,
    ]);

    return (
        <Table
            columns={columns}
            data={clients}
            allowSelect={allowSelect}
            onSelectActions={onSelectActions}
            stickyLastColumn
            handleRowStyle={(row) =>
                getClientRowStylesForInquiryMethod(row.original.inquiry_method)
            }
        />
    );
}
